import GetApp from '@mui/icons-material/GetApp';
import { Box, IconButton, Skeleton, Stack } from '@mui/material';
import { downloadFilesFromRequest, getFilesFromRequest } from 'lib/api/files';
import type { RequestsFilesResponse } from 'lib/api/files/types';
import { getShortString } from 'lib/functions/utils';
import {
  ErrorState,
  MobileBody,
  MobileCaption,
  MobileTitle,
} from 'lib/mobileDesignSystem';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export type FinanceFilesRequestType = 'factoring' | 'confirming' | 'creditLine';

export interface SharedFilesDrawerProps {
  requestId: string | null;
  requestType: FinanceFilesRequestType;
}

const SharedFilesDrawer: React.FC<SharedFilesDrawerProps> = ({
  requestId,
  requestType,
}) => {
  const dispatch = useDispatch();

  const [fileNames, setFileNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloadingFileName, setDownloadingFileName] = useState<string | null>(
    null,
  );

  const handleDownload = useCallback(
    async (fileName: string) => {
      if (requestId == null || requestId === '') return;
      setDownloadingFileName(fileName);
      try {
        const response = await downloadFilesFromRequest(dispatch, {
          requestType,
          requestId,
          fileName,
        });
        if (response) {
          window.open(response);
        }
      } finally {
        setDownloadingFileName(null);
      }
    },
    [dispatch, requestId, requestType],
  );

  const fetchFiles = useCallback(async () => {
    if (requestId == null || requestId === '') return;
    setLoading(true);
    setError(false);
    try {
      const response = await getFilesFromRequest(dispatch, {
        requestType,
        requestId,
      });
      const names = Array.isArray(response)
        ? response
        : ((response as RequestsFilesResponse)?.fileNames ?? []);
      setFileNames(names);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, requestId, requestType]);

  useEffect(() => {
    if (requestId != null && requestId !== '') {
      void fetchFiles();
    } else {
      setLoading(false);
    }
  }, [requestId, fetchFiles]);

  if (requestId == null || requestId === '') {
    return (
      <ErrorState message="No se pudo cargar los archivos de esta operación." />
    );
  }

  if (error) {
    return <ErrorState message="Error al cargar el listado de archivos." />;
  }

  if (loading) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1,
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <MobileTitle gutterBottom>Archivos</MobileTitle>
        </Box>
        <Stack spacing={1} sx={{ pt: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="text"
              width="80%"
              height={24}
              sx={{ fontSize: '0.875rem' }}
            />
          ))}
        </Stack>
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1,
          pb: 2,
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <MobileTitle gutterBottom>Archivos</MobileTitle>
      </Box>
      {fileNames.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <MobileBody color="text.secondary">
            No hay archivos subidos para esta operación.
          </MobileBody>
        </Box>
      ) : (
        <Stack
          spacing={0}
          component="ul"
          sx={{ listStyle: 'none', p: 0, m: 0 }}
        >
          {fileNames.map((name, index) => (
            <Box
              key={`${name}-${index}`}
              component="li"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.25,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <MobileCaption
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {getShortString(name, 40)}
              </MobileCaption>
              <IconButton
                onClick={() => void handleDownload(name)}
                disabled={downloadingFileName === name}
                aria-label={`Descargar ${name}`}
                size="small"
                sx={{
                  color: 'primary.main',
                  flexShrink: 0,
                }}
              >
                <GetApp fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </>
  );
};

export default SharedFilesDrawer;
